import React, { useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';


import Avatar from '@mui/material/Avatar';
import Tooltip from '@mui/material/Tooltip';
import { useTheme } from '@mui/material/styles';
import type { IssueCreate } from '../api-client/models/IssueCreate';
import { UrgencyLevel } from '../api-client/models/UrgencyLevel';
import { AdminService } from '../api-client/services/AdminService';
import { useLogger } from './Logger';
import { useIMessage, useINotify, Severity } from './iNotify';
import BugReportIcon from '@mui/icons-material/BugReport';
const initialIssue: IssueCreate = {
  title: '',
  body: '',
  is_bug: false,
  level: UrgencyLevel.LOW,
};

const IssueDialogComponent: React.FC = () => {
  const commit = import.meta.env.GIT_COMMIT;
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [issue, setIssue] = useState<IssueCreate>(initialIssue);
    const logger = useLogger();
    const imessage = useIMessage();
    const inotify = useINotify();
  const handleOpen = () => setOpen(true);

  const handleClose = () => {
    setIssue(initialIssue);
    setOpen(false);
  };

  const handleCreateIssue = async () => {
    if (!issue.title?.trim()) {
      return;
    }

    let _title = issue.title.trim();
    if (commit && issue.is_bug) {
      _title = `${_title} [${commit}]`;
    }

    let _body = issue.body?.trim() ?? '';
    if (!_body) {
        _body = _title;
    }

    const payload: IssueCreate = {
      title: _title,
      body: _body,
      is_bug: issue.is_bug ?? false,
      level: issue.level ?? UrgencyLevel.LOW,
    };

    try {        
      handleClose();
      setSubmitting(true);
      const response = await AdminService.postGithubIssue(payload);
      //logger.info('Issue created successfully:', response);
      imessage({ message: 'Issue created successfully', severity: Severity.SUCCESS, usei18n: false });
    } catch (error) {
      logger.error('Failed to create issue:', error);
      inotify({ message: 'Failed to create issue', severity: Severity.ERROR, usei18n: false });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* <Button variant="outlined" onClick={handleOpen} startIcon={<BugReportIcon />}>
        Issue
      </Button> */}
        <Tooltip title={'Create Issue'}>
        <Avatar
          variant="rounded"
          sx={{
            ...theme.typography.commonAvatar,
            ...theme.typography.mediumAvatar,
            transition: 'all .2s ease-in-out',
            color: theme.vars.palette.primary.dark,
            background: theme.vars.palette.primary.light,
            '&:hover': {
              color: theme.vars.palette.primary.light,
              background: theme.vars.palette.primary.main
            },

            ...theme.applyStyles('dark', {
              color: theme.vars.palette.primary.dark,
              background: theme.vars.palette.dark.main,
              '&:hover': {
                color: theme.vars.palette.primary.light,
                background: theme.vars.palette.primary.main
              }
            })
          }}
          aria-controls={open ? 'menu-list-grow' : undefined}
          aria-haspopup="true"
          onClick={handleOpen}
        >
          {/* {open ? <IconArrowsMinimize /> : <IconArrowsMaximize />} */}
          <BugReportIcon />
        </Avatar>
      </Tooltip>



      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Create Issue</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Subject"
              required
              fullWidth
              value={issue.title}
              onChange={(e) => setIssue({ ...issue, title: e.target.value })}
            />

            <TextField
              label="Description (Markdown supported)"
              fullWidth
              multiline
              rows={4}
              value={issue.body ?? ''}
              onChange={(e) => setIssue({ ...issue, body: e.target.value })}
            />

            <FormControl fullWidth>
              <InputLabel id="issue-level-label">Severity Level</InputLabel>
              <Select
                labelId="issue-level-label"
                label="Severity Level"
                value={issue.level ?? UrgencyLevel.LOW}
                onChange={(e) =>
                  setIssue({
                    ...issue,
                    level: e.target.value as UrgencyLevel,
                  })
                }
              >
                <MenuItem value={UrgencyLevel.BLOCKING}>blocking</MenuItem>
                <MenuItem value={UrgencyLevel.IMPORTANT}>important</MenuItem>
                <MenuItem value={UrgencyLevel.LOW}>low</MenuItem>
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Checkbox
                  checked={issue.is_bug ?? false}
                  onChange={(e) => setIssue({ ...issue, is_bug: e.target.checked })}
                />
              }
              label="Is this a bug?"
            />
            <Typography variant="body2" color="textSecondary">
              Commit: {commit}
            </Typography  >
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleCreateIssue}
            variant="contained"
            disabled={submitting || !issue.title?.trim()}
            startIcon={submitting ? <CircularProgress size={18} /> : undefined}
          >
            Create issue
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default IssueDialogComponent;
